import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { guestCheckoutUsuario } from '@/api/usuariosApi'
import type { GuestCheckoutResult } from '@/api/usuariosApi'
import { COUNTRY_OPTIONS, type CountryCode, buildPhoneForStorage, formatPhone } from '@/utils/phone'
import styles from './GuestCadastroModal.module.css'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MODAL_OPEN_CLASS = 'app-modal-open'

type ViewportBox = { top: number; height: number }

function readVisualViewport(): ViewportBox {
  const vv = window.visualViewport
  if (!vv) {
    return { top: 0, height: window.innerHeight }
  }
  return { top: vv.offsetTop, height: vv.height }
}

export interface GuestCadastroModalProps {
  open: boolean
  loading?: boolean
  onCancel: () => void
  onSuccess: (result: GuestCheckoutResult) => void | Promise<void>
}

export function GuestCadastroModal({ open, loading: externalLoading, onCancel, onSuccess }: GuestCadastroModalProps) {
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [telefonePais, setTelefonePais] = useState<CountryCode>('PT')
  const [erro, setErro] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [viewport, setViewport] = useState<ViewportBox>(() =>
    typeof window !== 'undefined' ? readVisualViewport() : { top: 0, height: 800 }
  )

  useEffect(() => {
    if (!open) return

    document.documentElement.classList.add(MODAL_OPEN_CLASS)

    const sync = () => setViewport(readVisualViewport())
    sync()

    const vv = window.visualViewport
    vv?.addEventListener('resize', sync)
    vv?.addEventListener('scroll', sync)
    window.addEventListener('resize', sync)

    return () => {
      document.documentElement.classList.remove(MODAL_OPEN_CLASS)
      vv?.removeEventListener('resize', sync)
      vv?.removeEventListener('scroll', sync)
      window.removeEventListener('resize', sync)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setEmail('')
      setTelefone('')
      setTelefonePais('PT')
      setErro('')
      setSubmitting(false)
    }
  }, [open])

  if (!open) return null

  const loading = externalLoading || submitting

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')

    const emailTrim = email.trim()
    if (!emailTrim) {
      setErro('Indique o seu e-mail.')
      return
    }
    if (!EMAIL_REGEX.test(emailTrim)) {
      setErro('Formato de e-mail inválido.')
      return
    }
    if (!telefone.trim()) {
      setErro('Indique um telefone para contacto.')
      return
    }
    if (telefone.replace(/\D/g, '').length < 9) {
      setErro('Telefone incompleto. Confira o indicativo e os dígitos.')
      return
    }

    setSubmitting(true)
    try {
      const checkout = await guestCheckoutUsuario({
        email: emailTrim,
        telefone: buildPhoneForStorage(telefone, telefonePais),
      })
      await onSuccess(checkout)
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Não foi possível confirmar os dados.')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div
      className={styles.backdrop}
      role="presentation"
      style={{ top: viewport.top, height: viewport.height }}
      onClick={loading ? undefined : onCancel}
    >
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-cadastro-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.handle} aria-hidden />

        <header className={styles.header}>
          <h2 id="guest-cadastro-title" className={styles.title}>
            Contacto para a marcação
          </h2>
          <p className={styles.message}>
            Indique e-mail e telefone para confirmar a marcação e receber atualizações.
          </p>
        </header>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fields}>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="guest-email">
                E-mail
              </label>
              <input
                id="guest-email"
                type="email"
                className={styles.input}
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setErro('')
                }}
                autoComplete="email"
                inputMode="email"
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="guest-telefone">
                Telefone
              </label>
              <div className={styles.phoneRow}>
                <select
                  id="guest-telefone-pais"
                  className={styles.select}
                  value={telefonePais}
                  aria-label="País do telefone"
                  onChange={(e) => {
                    const code = e.target.value as CountryCode
                    setTelefonePais(code)
                    if (telefone.trim()) setTelefone(formatPhone(telefone, code))
                  }}
                >
                  {COUNTRY_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <input
                  id="guest-telefone"
                  type="tel"
                  className={styles.input}
                  placeholder="912 345 678"
                  value={telefone}
                  onChange={(e) => {
                    setTelefone(e.target.value ? formatPhone(e.target.value, telefonePais) : '')
                    setErro('')
                  }}
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>
            </div>

            {erro && (
              <p className={styles.erro} role="alert">
                {erro}
              </p>
            )}
          </div>

          <div className={styles.actions}>
            <button type="submit" className={styles.confirmBtn} disabled={loading}>
              {loading ? 'A confirmar…' : 'Confirmar'}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={onCancel} disabled={loading}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}
